export const COMBO_FLOW_SLUG = 'online-insurance-quote';
export const COMBO_FLOW_CATEGORY = 'Bundle';
export const COMBO_FLOW_PRODUCT = 'CombinedQuotes';
export const COMBO_RESULTS_PATH = '/combinedInsuranceQuoteResults';

export const COMBO_PRODUCT_OPTIONS = [
  {
    label: 'Car Insurance',
    category: 'Vehicle',
    product: 'CarInsurance',
  },
  {
    label: 'Motorcycle Insurance',
    category: 'Vehicle',
    product: 'MotorcycleInsurance',
  },
  {
    label: 'Building Insurance',
    category: 'Home',
    product: 'BuildingInsurance',
  },
  {
    label: 'Home Contents Insurance',
    category: 'Home',
    product: 'HomeContentsCover',
  },
];

export const COMBO_SHARED_FIELDS = [
  'fullName',
  'idNumber',
  'phone',
  'email',
  'maritalStatus',
  'birthday',
  'gender',
  'hippoMarketingOptIn',
  'FirstName',
  'LastName',
  'firstName',
  'lastName',
  'CellNumber',
  'cellNumber',
];

const COMBO_SHARED_FIELD_SET = new Set(COMBO_SHARED_FIELDS);

const splitFullName = (fullName = '') => {
  const [firstName = '', ...rest] = String(fullName).trim().split(/\s+/);
  return {
    firstName,
    lastName: rest.join(' '),
  };
};

const hasMeaningfulSeedValue = (value) => {
  if (Array.isArray(value)) {
    return value.some((entry) => hasMeaningfulSeedValue(entry));
  }

  if (value && typeof value === 'object') {
    return Object.values(value).some((entry) => hasMeaningfulSeedValue(entry));
  }

  return value !== undefined && value !== null && String(value).trim() !== '';
};

const cloneSeedValue = (value) => {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...value };
  return value;
};

const copySeedFields = (source = {}, fields = []) =>
  fields.reduce((acc, field) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) return acc;

    const value = source[field];
    if (!hasMeaningfulSeedValue(value)) return acc;

    acc[field] = cloneSeedValue(value);
    return acc;
  }, {});

const COMBO_FLOW_SEED_FIELDS = {
  CarInsurance: [
    'carDetails',
    'yearOfManufacturer',
    'nightAddress',
    'nightSecurity',
    'dayAddress',
    'daySecurity',
    'vehicleUsage',
    'mainDriver',
    'mainDriverLicense',
    'licenseFirstIssue',
    'uninterruptedCarInsurance',
    'lastClaimYears',
    'mainDriverDetails',
    'additionalDriverFullName',
    'additionalDriverGender',
    'additionalDriverBirthday',
    'additionalDriverMaritalStatus',
    'additionalDriverDriverLicense',
    'additionalDriverLicenseFirstIssue',
    'additionalDriverUninterruptedCarInsurance',
    'additionalDriverLastClaimYears',
  ],
  MotorcycleInsurance: [
    'yearOfMotorCycleManufacturer',
    'motorCycleDetails',
    'bikeValue',
    'nightAddress',
    'nightSecurity',
    'mainDriver',
    'mainDriverLicense',
    'licenseFirstIssue',
    'maritalStatus',
    'uninterruptedBikeInsurance',
    'mainDriverDetails',
    'additionalDriverFullName',
    'additionalDriverGender',
    'additionalDriverBirthday',
    'additionalDriverMaritalStatus',
    'additionalDriverDriverLicense',
    'additionalDriverLicenseFirstIssue',
    'additionalDriverUninterruptedBikeInsurance',
  ],
  BuildingInsurance: ['homeValue', 'buildingAddress', 'numberOfGeysers'],
  HomeContentsCover: [
    'homeContentValue',
    'moveIntoHome',
    'homeBreakIns',
    'breakInHappen',
    'extraSecurityFeatures',
    'occupiedDuringDay',
    'homeBordered',
    'homeAddress',
  ],
};

export const buildComboFlowSeed = (formValues = {}, product) => {
  const fields = COMBO_FLOW_SEED_FIELDS[product] || [];
  if (!fields.length) return {};
  return copySeedFields(formValues, fields);
};

export const isComboFlowSlug = (flowSlug) => flowSlug === COMBO_FLOW_SLUG;

export const isComboProduct = (product) => product === COMBO_FLOW_PRODUCT;

export const isComboSharedField = (fieldName = '') =>
  COMBO_SHARED_FIELD_SET.has(String(fieldName).split('.')[0]);

export const getComboProductByLabel = (label) =>
  COMBO_PRODUCT_OPTIONS.find((option) => option.label === label) || null;

export const getComboProductByProduct = (product) =>
  COMBO_PRODUCT_OPTIONS.find((option) => option.product === product) || null;

export const buildComboJourney = (selectedLabels = []) => {
  const selectedSet = new Set(selectedLabels);
  const queue = COMBO_PRODUCT_OPTIONS.filter((option) =>
    selectedSet.has(option.label)
  ).map(({ category, product, label }) => ({
    category,
    product,
    label,
  }));

  return {
    isActive: queue.length > 0,
    queue,
    selectedProducts: queue.map((item) => item.product),
    currentFlowIndex: 0,
  };
};

export const getComboJourney = (formValues = {}) =>
  formValues.comboJourney || {};

export const getSelectedComboProducts = (formValues = {}) => {
  const queuedProducts = formValues?.comboJourney?.selectedProducts;
  if (Array.isArray(queuedProducts) && queuedProducts.length > 0) {
    return queuedProducts;
  }

  const selectedLabels = Array.isArray(formValues?.comboSelection)
    ? formValues.comboSelection
    : [];

  return selectedLabels
    .map((label) => getComboProductByLabel(label)?.product)
    .filter(Boolean);
};

export const getComboFlowValues = (formValues = {}, product) => {
  if (!product || isComboProduct(product)) return {};
  const existingFlow = formValues.comboFlows?.[product] || {};
  if (hasMeaningfulSeedValue(existingFlow)) return existingFlow;
  return buildComboFlowSeed(formValues, product);
};

export const getComboProjectedValues = (formValues = {}, product) => {
  const sharedValues = COMBO_SHARED_FIELDS.reduce((acc, field) => {
    if (formValues[field] !== undefined) {
      acc[field] = formValues[field];
    }
    return acc;
  }, {});

  const flowValues = getComboFlowValues(formValues, product);

  return {
    ...sharedValues,
    ...flowValues,
    comboSelection: Array.isArray(formValues.comboSelection)
      ? formValues.comboSelection
      : [],
    comboJourney: formValues.comboJourney || null,
  };
};

export const applyComboFieldChange = (
  formValues = {},
  activeProduct,
  name,
  value
) => {
  const nextFormValues = {
    ...formValues,
    comboFlows: { ...(formValues.comboFlows || {}) },
  };

  if (name === 'comboSelection') {
    nextFormValues.comboSelection = Array.isArray(value) ? value : [];
    return nextFormValues;
  }

  if (
    !activeProduct ||
    isComboProduct(activeProduct) ||
    isComboSharedField(name)
  ) {
    nextFormValues[name] = value;

    if (name === 'fullName') {
      const { firstName, lastName } = splitFullName(value);
      nextFormValues.FirstName = firstName;
      nextFormValues.LastName = lastName;
    }

    return nextFormValues;
  }

  nextFormValues.comboFlows[activeProduct] = {
    ...(formValues.comboFlows?.[activeProduct] || {}),
    [name]: value,
  };

  return nextFormValues;
};

export const mergeIntoComboFormValues = (
  formValues = {},
  activeProduct,
  incomingValues = {}
) => {
  let nextFormValues = {
    ...formValues,
    comboFlows: { ...(formValues.comboFlows || {}) },
  };

  Object.entries(incomingValues).forEach(([key, value]) => {
    if (key === 'comboJourney' || key === 'comboFlows') return;
    nextFormValues = applyComboFieldChange(
      nextFormValues,
      activeProduct,
      key,
      value
    );
  });

  return nextFormValues;
};

export const setComboJourneyState = (formValues = {}, comboJourney) => ({
  ...formValues,
  comboJourney,
  comboFlows: { ...(formValues.comboFlows || {}) },
});

export const seedComboFlowsFromFormValues = (
  formValues = {},
  selectedProducts = []
) => {
  const products = Array.isArray(selectedProducts) ? selectedProducts : [];

  if (!products.length) {
    return {
      ...formValues,
      comboFlows: { ...(formValues.comboFlows || {}) },
    };
  }

  const nextComboFlows = { ...(formValues.comboFlows || {}) };

  products.forEach((product) => {
    const seed = buildComboFlowSeed(formValues, product);
    if (!hasMeaningfulSeedValue(seed)) return;

    nextComboFlows[product] = {
      ...seed,
      ...(nextComboFlows[product] || {}),
    };
  });

  return {
    ...formValues,
    comboFlows: nextComboFlows,
  };
};

export const advanceComboJourney = (formValues = {}) => {
  const comboJourney = getComboJourney(formValues);
  return setComboJourneyState(formValues, {
    ...comboJourney,
    currentFlowIndex: (comboJourney.currentFlowIndex || 0) + 1,
  });
};

export const getCurrentComboQueueItem = (formValues = {}) => {
  const comboJourney = getComboJourney(formValues);
  const index = comboJourney.currentFlowIndex || 0;
  return comboJourney.queue?.[index] || null;
};

export const getNextComboQueueItem = (formValues = {}) => {
  const comboJourney = getComboJourney(formValues);
  const nextIndex = (comboJourney.currentFlowIndex || 0) + 1;
  return comboJourney.queue?.[nextIndex] || null;
};

const getValueByPath = (obj, path) =>
  String(path)
    .split('.')
    .reduce(
      (acc, segment) =>
        acc && acc[segment] !== undefined ? acc[segment] : undefined,
      obj
    );

const hasValue = (value) => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') {
    return Object.values(value).every(
      (entry) => entry !== undefined && entry !== null && entry !== ''
    );
  }
  return value !== undefined && value !== null;
};

export const getQuestionFieldNames = (questionJson = {}) => {
  if (!Array.isArray(questionJson.questions)) return [];

  return questionJson.questions.flatMap((group) =>
    Object.entries(group)
      .filter(([key]) => key !== 'layout' && key !== 'columns')
      .map(([key, config]) => config?.name || key)
  );
};

export const shouldSkipComboQuestion = (
  questionJson = {},
  projectedValues = {}
) => {
  const fieldNames = getQuestionFieldNames(questionJson);

  if (!fieldNames.length) return false;
  if (!fieldNames.every((fieldName) => isComboSharedField(fieldName))) {
    return false;
  }

  return fieldNames.every((fieldName) =>
    hasValue(getValueByPath(projectedValues, fieldName))
  );
};
